import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import {
    Gift, CheckCircle2, Loader2, ArrowRight, ArrowLeft,
    KeyRound, BadgeCheck, Sparkles,
} from 'lucide-react';
import useSEO from '@/hooks/useSEO';

/**
 * /redeem — 4-step Gift Card redemption wizard.
 *
 *   Step 1  Enter code    → strips formatting, dashes every 4 chars
 *   Step 2  Preview       → server-side validates (does not redeem),
 *                            shows tier + months + giver note
 *   Step 3  Confirm       → POST /gift-cards/redeem
 *   Step 4  Celebrate     → unlock animation, link to dashboard
 *
 * Visited steps are tracked so the back button works in all directions.
 * If the user lands here unauthenticated, the wizard skips to a sign-in
 * nudge but preserves their entered code through ?next= roundtrip.
 */
const STEPS = [
    { idx: 1, label: 'Enter code',  icon: KeyRound },
    { idx: 2, label: 'Preview',     icon: BadgeCheck },
    { idx: 3, label: 'Confirm',     icon: Gift },
    { idx: 4, label: 'Celebrate',   icon: Sparkles },
];

function formatCode(raw) {
    const txt = (raw || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    return (txt.match(/.{1,4}/g) || []).join('-').slice(0, 19);
}

export default function Redeem() {
    useSEO({
        title: 'Redeem a gift card — Linguistic Quest',
        description: 'Redeem your Linguistic Quest gift card to unlock paid features.',
        path: '/redeem',
    });
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const initialCode = formatCode(params.get('code') || '');
    const [step, setStep] = useState(1);
    const [code, setCode] = useState(initialCode);
    const [preview, setPreview] = useState(null);
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const successAudio = useRef(null);

    // If the URL pre-supplied a code, automatically advance past the entry step
    // (parents often share /redeem?code=XXXX in family chats). We MUST wait
    // for the AuthContext to finish hydrating `user` before deciding whether
    // to call /gift-cards/preview (which needs auth) or to bounce to login —
    // otherwise a freshly-logged-in user lands here, sees `user === null` for
    // one render, gets redirected back to /login, and the wizard never starts.
    useEffect(() => {
        if (authLoading) return;
        if (!initialCode) return;
        if (initialCode.replace(/-/g, '').length < 12) return;
        if (step !== 1 || preview || busy) return;
        tryPreview(initialCode);
    }, [authLoading, user]);

    const tryPreview = async (rawCode) => {
        if (authLoading) return;  // ignore clicks while AuthContext hydrates
        if (!user) {
            // Round-trip through login. URL-encode the WHOLE next path so the
            // second `?code=…` doesn't get parsed as a sibling param of `next`.
            const next = `/redeem?code=${encodeURIComponent(rawCode)}`;
            navigate(`/login?next=${encodeURIComponent(next)}`);
            return;
        }
        setErr(null); setBusy(true);
        try {
            const { data } = await api.post('/gift-cards/preview', { code: rawCode });
            setPreview(data);
            setStep(2);
        } catch (e) {
            // Surface the underlying error in the dev console so we can debug
            // user-reported "no stage after inputting voucher" reports.
            console.error('[redeem] preview failed', e?.response?.status, e?.response?.data);
            setErr(e?.response?.data?.detail || 'We could not find that code. Double-check it and try again.');
        } finally { setBusy(false); }
    };

    const doRedeem = async () => {
        setErr(null); setBusy(true);
        try {
            const { data } = await api.post('/gift-cards/redeem', { code });
            setResult(data);
            setStep(4);
            // gentle confirmation tick (optional, may fail silently)
            try {
                if (successAudio.current) {
                    successAudio.current.currentTime = 0;
                    await successAudio.current.play();
                }
            } catch { /* user may have audio blocked — non-essential */ }
        } catch (e) {
            setErr(e?.response?.data?.detail || 'Redeem failed.');
            setStep(3);
        } finally { setBusy(false); }
    };

    return (
        <main className="max-w-3xl mx-auto px-6 py-12" data-testid="page-redeem">
            <div className="flex items-center gap-2 text-lq-purple-500 font-semibold text-xs uppercase tracking-wider">
                <Gift size={14}/> Gift card
            </div>
            <h1 className="mt-1 font-display text-4xl md:text-5xl font-semibold text-lq-ink-black">Unwrap your access.</h1>
            <p className="mt-3 text-lq-ink-700">Four small steps. We will preview your gift before activating, so there are no surprises.</p>

            <Stepper current={step}/>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
                <StepBox idx={1} active={step === 1} done={step > 1} label="Enter code" icon={KeyRound}/>
                <StepBox idx={2} active={step === 2} done={step > 2} label="Preview" icon={BadgeCheck}/>
                <StepBox idx={3} active={step === 3} done={step > 3} label="Confirm" icon={Gift}/>
                <StepBox idx={4} active={step === 4} done={false} label="Celebrate" icon={Sparkles}/>
            </div>

            <section className="mt-8 bg-white border border-lq-ink-100 rounded-2xl p-6 sm:p-8">
                {step === 1 && (
                    <StepEnter code={code} setCode={setCode} busy={busy} err={err}
                               onNext={() => tryPreview(code)} requiresLogin={!user}/>
                )}
                {step === 2 && preview && (
                    <StepPreview code={code} preview={preview} onBack={() => { setStep(1); setPreview(null); }} onNext={() => setStep(3)}/>
                )}
                {step === 3 && preview && (
                    <StepConfirm code={code} preview={preview} busy={busy} err={err}
                                 onBack={() => setStep(2)} onConfirm={doRedeem}/>
                )}
                {step === 4 && result && (
                    <StepCelebrate result={result} onDashboard={() => navigate('/dashboard')}/>
                )}
            </section>
        </main>
    );
}


function Stepper({ current }) {
    return (
        <div className="mt-6 hidden sm:flex items-center gap-3 text-xs text-lq-ink-500">
            {STEPS.map((s, i) => (
                <React.Fragment key={s.idx}>
                    <span className={`inline-flex items-center gap-1.5 ${current === s.idx ? 'text-lq-purple-700 font-semibold' : current > s.idx ? 'text-lq-success' : ''}`} data-testid={`stepper-${s.idx}`}>
                        <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-[11px] font-bold ${current === s.idx ? 'bg-lq-purple-500 text-white' : current > s.idx ? 'bg-lq-success text-white' : 'bg-lq-ink-100 text-lq-ink-500'}`}>
                            {current > s.idx ? '✓' : s.idx}
                        </span>
                        {s.label}
                    </span>
                    {i < STEPS.length - 1 && <span className="h-px w-8 bg-lq-ink-300"/>}
                </React.Fragment>
            ))}
        </div>
    );
}

function StepBox({ idx, active, done, label, icon: Icon }) {
    let cls = 'bg-lq-ink-100/40 text-lq-ink-500';
    if (active) cls = 'bg-lq-purple-50 border-2 border-lq-purple-500 text-lq-purple-900 shadow-sm';
    else if (done) cls = 'bg-lq-success/10 border border-lq-success text-lq-success';
    return (
        <div className={`rounded-2xl p-4 ${cls}`} data-testid={`step-box-${idx}`}>
            <div className="flex items-center gap-2">
                <span className={`inline-flex w-9 h-9 rounded-xl items-center justify-center ${active ? 'bg-lq-purple-500 text-white' : done ? 'bg-lq-success text-white' : 'bg-white text-lq-ink-500'}`}>
                    {done ? <CheckCircle2 size={18}/> : <Icon size={18}/>}
                </span>
                <div>
                    <div className="text-[10px] uppercase tracking-wider opacity-70">Step {idx}</div>
                    <div className="font-display font-semibold">{label}</div>
                </div>
            </div>
        </div>
    );
}


function StepEnter({ code, setCode, busy, err, onNext, requiresLogin }) {
    return (
        <div data-testid="step-enter">
            <h2 className="font-display text-2xl text-lq-ink-900">Step 1 — Enter your code</h2>
            <p className="mt-1 text-sm text-lq-ink-500">It looks like <code className="font-mono">XXXX-XXXX-XXXX-XX</code>. Spaces and dashes are fine, we will tidy them up.</p>
            <input
                value={code}
                onChange={(e) => setCode(formatCode(e.target.value))}
                onPaste={(e) => {
                    e.preventDefault();
                    setCode(formatCode(e.clipboardData?.getData('text') || ''));
                }}
                placeholder="XXXX-XXXX-XXXX-XX"
                className="mt-4 w-full text-center font-mono tracking-widest text-2xl py-4 rounded-xl border-2 border-lq-ink-300 focus:border-lq-purple-500"
                data-testid="redeem-code"
                autoFocus
            />
            {err && <div className="mt-3 text-sm text-lq-error" role="alert" data-testid="redeem-error">{err}</div>}
            {requiresLogin && (
                <p className="mt-3 text-xs text-lq-ink-500">
                    You will sign in next so we can attach the gift to your account.
                    Don't have an account yet? <Link to="/register?next=/redeem" className="text-lq-purple-700 underline">Sign up free first</Link>.
                </p>
            )}
            <div className="mt-6 flex justify-end">
                <button
                    onClick={onNext}
                    disabled={busy || code.replace(/-/g, '').length < 12}
                    className="px-6 py-3 bg-lq-purple-500 text-white font-semibold rounded-full hover:bg-lq-purple-700 disabled:opacity-50 inline-flex items-center gap-2"
                    data-testid="step-1-next"
                >
                    {busy && <Loader2 size={16} className="animate-spin"/>} Preview gift <ArrowRight size={14}/>
                </button>
            </div>
        </div>
    );
}


function StepPreview({ code, preview, onBack, onNext }) {
    return (
        <div data-testid="step-preview">
            <h2 className="font-display text-2xl text-lq-ink-900">Step 2 — Preview</h2>
            <p className="mt-1 text-sm text-lq-ink-500">Make sure this is what you expected — nothing is activated yet.</p>

            <div className="mt-5 bg-lq-purple-50 border border-lq-purple-100 rounded-2xl p-5">
                <div className="text-[11px] uppercase tracking-wider text-lq-purple-700 font-semibold">Your gift</div>
                <div className="mt-1 flex items-baseline gap-2 flex-wrap">
                    <span className="font-display text-3xl text-lq-ink-900 capitalize">{(preview.tier || '').toString()} tier</span>
                    <span className="text-lq-ink-500">·</span>
                    <span className="font-display text-2xl text-lq-purple-700">{preview.months} month{preview.months === 1 ? '' : 's'}</span>
                </div>
                {preview.giver_name && (
                    <p className="mt-2 text-sm text-lq-ink-700">From <strong>{preview.giver_name}</strong>{preview.giver_message ? `: “${preview.giver_message}”` : '.'}</p>
                )}
                <div className="mt-3 text-xs text-lq-ink-500 font-mono">Code: {code}</div>
            </div>
            <div className="mt-6 flex justify-between">
                <button onClick={onBack} className="px-4 py-2 text-sm rounded-full bg-white border border-lq-ink-300 text-lq-ink-900 inline-flex items-center gap-1" data-testid="step-2-back">
                    <ArrowLeft size={14}/> Re-enter code
                </button>
                <button onClick={onNext} className="px-6 py-3 bg-lq-purple-500 text-white font-semibold rounded-full hover:bg-lq-purple-700 inline-flex items-center gap-2" data-testid="step-2-next">
                    Continue <ArrowRight size={14}/>
                </button>
            </div>
        </div>
    );
}


function StepConfirm({ code, preview, busy, err, onBack, onConfirm }) {
    const [ack, setAck] = useState(false);
    return (
        <div data-testid="step-confirm">
            <h2 className="font-display text-2xl text-lq-ink-900">Step 3 — Confirm activation</h2>
            <p className="mt-1 text-sm text-lq-ink-500">Once you activate, the code cannot be re-used.</p>

            <div className="mt-5 bg-lq-paper border border-lq-ink-100 rounded-2xl p-5 space-y-2 text-sm">
                <Row label="Tier"        value={<span className="capitalize">{preview.tier}</span>}/>
                <Row label="Duration"    value={`${preview.months} month${preview.months === 1 ? '' : 's'}`}/>
                <Row label="Code"        value={<span className="font-mono">{code}</span>}/>
                {preview.expires_at && <Row label="Code expires" value={new Date(preview.expires_at).toLocaleDateString()}/>}
            </div>

            <label className="mt-4 inline-flex items-start gap-2 text-sm text-lq-ink-700 cursor-pointer">
                <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="redeem-ack" className="mt-0.5 w-4 h-4"/>
                <span>I understand this gift will be activated on my account and the code becomes single-use.</span>
            </label>

            {err && <div className="mt-3 text-sm text-lq-error" role="alert" data-testid="redeem-error">{err}</div>}

            <div className="mt-6 flex justify-between">
                <button onClick={onBack} className="px-4 py-2 text-sm rounded-full bg-white border border-lq-ink-300 text-lq-ink-900 inline-flex items-center gap-1" data-testid="step-3-back">
                    <ArrowLeft size={14}/> Back
                </button>
                <button onClick={onConfirm} disabled={busy || !ack} className="px-6 py-3 bg-lq-success text-white font-semibold rounded-full hover:bg-lq-success/90 disabled:opacity-50 inline-flex items-center gap-2" data-testid="step-3-confirm">
                    {busy && <Loader2 size={16} className="animate-spin"/>} Activate gift
                </button>
            </div>
        </div>
    );
}


function StepCelebrate({ result, onDashboard }) {
    return (
        <div className="text-center" data-testid="step-celebrate">
            <div className="mx-auto h-16 w-16 rounded-full bg-lq-success/15 inline-flex items-center justify-center text-lq-success quiz-burst quiz-burst-correct">
                <CheckCircle2 size={36}/>
            </div>
            <h2 className="mt-4 font-display text-3xl text-lq-ink-900">Activated 🎉</h2>
            <p className="mt-2 text-lq-ink-700">
                <span className="capitalize">{result.tier}</span> tier · <strong>{result.months} month{result.months === 1 ? '' : 's'}</strong>
                {result.expires_at && <> · access runs through {new Date(result.expires_at).toLocaleDateString()}</>}.
            </p>
            <p className="mt-4 text-sm text-lq-ink-500 italic">
                Pace, not pressure — your family decides when to dive in.
            </p>
            <button onClick={onDashboard} className="mt-6 px-6 py-3 bg-lq-purple-500 text-white font-semibold rounded-full hover:bg-lq-purple-700 inline-flex items-center gap-2" data-testid="step-4-dashboard">
                Open the dashboard <ArrowRight size={14}/>
            </button>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-lq-ink-500">{label}</span>
            <span className="text-lq-ink-900 font-medium">{value}</span>
        </div>
    );
}
